import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { Phone } from "lucide-react";
import { Link } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import { useQuery } from "@tanstack/react-query";

const BASE_URL = import.meta.env.VITE_API_URL;

interface Props {
  isLoading: boolean;
  onSubmit: ({phone_number, country_code}:{phone_number: string, country_code : string}) => void;
}

interface PropsCountry{
  id: number;
  code: string;
  name: string;
}

const fetchCountryCode = async() => {
  try {
    const res = await fetch(`${BASE_URL}/mobile/country-code`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
    return await res.json()
  } catch (error) {
    console.error("Error:", error);
  }
}

const PhoneInput: React.FC<Props> = ({ onSubmit, isLoading }) => {
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  
  const {data : fetchData} = useQuery({
    queryKey: ["country-code"],
    queryFn: fetchCountryCode,
    staleTime: 1000 * 60 * 5
  })

  const [selectedCountry, setSelectedCountry] = useState({ code: "82" });
  const [open, setOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    let valid = true;

    if (!phone || phone.length < 6) {
      const msg = "유효하지 않은 전화번호입니다 (최소 9자리 이상)";
      setError("이 전화번호는 이미 사용 중입니다");
      Swal.fire({
        icon: "warning",
        title: "이 전화번호는 이미 사용 중입니다",
        text: msg,
        confirmButtonColor: "#C8EDFF",
        customClass: {
          confirmButton: "px-6 py-2 bg-red-500 text-white",
        },
      });
      valid = false;
    } else {
      setError("");
    }

    if (valid) {
      let dataSubmit = {
        phone_number: phone,
        country_code : selectedCountry.code
      }
      onSubmit(dataSubmit);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToCenter = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const scrollY =
        window.scrollY + rect.top - window.innerHeight / 2 + rect.height / 2;
      window.scrollTo({ top: scrollY, behavior: "smooth" });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col items-center space-y-5 w-full mx-auto "
    >
      <Link to={"/login"} className="absolute top-16 left-4 text-2xl">
        ←
      </Link>
      <div className="items-center justify-center mt-10 bg-gray-100 rounded-full p-2">
        <Phone className="w-16 h-16 p-2 grayscale opacity-20" />
      </div>
      <div className="mt-10">
        <h2 className="font-bold text-[16px] text-center">
          전화번호를 입력해주세요
        </h2>
      </div>
      <div className="relative w-full">
        <div
          className={`flex items-center border  overflow-hidden border bg-gray-100 ${
            error ? "border-red-500" : "border-gray-300"
          }`}
        >
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex items-center px-3 py-2 gap-1 text-sm bg-gray-100 outline-none mr-2"
          >
            {/* <span>{selectedCountry.flag}</span> */}
            <span>+{selectedCountry?.code}</span>
            <svg
              className={`w-4 h-4 transition-transform ${
                open ? "rotate-180" : "rotate-0"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          <input
            ref={inputRef}
            onFocus={scrollToCenter}
            type="number"
            placeholder="010-0000-0000"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="flex-1 text-[16px] outline-none bg-white py-2 pr-4"
          />
        </div>
        {open && (
          <ul className="absolute left-0 mt-1 w-max bg-white border border-gray-300 rounded shadow-lg z-20 max-h-48 overflow-auto">
            {fetchData?.data.slice()
              .sort((a: { name: string; }, b: { name: any; }) => a.name.localeCompare(b.name))
              .map((country: PropsCountry) => (
                <li
                  key={country.code}
                  className="flex items-center px-3 py-2 cursor-pointer hover:bg-blue-100"
                  onClick={() => {
                    setSelectedCountry(country);
                    setOpen(false);
                  }}
                >
                  <span>{country.name}</span>
                  <span className="ml-2 text-gray-500">+{country.code}</span>
                </li>
              ))}
          </ul>
        )}

        {error && (
          <p className="text-red-500 text-xs mt-1 text-center">{error}</p>
        )}

        <div className="w-full text-center mt-20">
          <button
            onClick={handleSubmit}
            type="button"
            className="text-white shadow-md bg-[#384D92] border p-2 mt-2 text-xs mt-1 text-center item-center w-full"
          >
            {!isLoading ? <>휴대폰 인증하기</> : <ClipLoader size={16}/>}
            
          </button>
        </div>
      </div>
    </form>
  );
};

export default PhoneInput;
